import {useCallback, useReducer} from 'react';
import type {ItemPurpose} from '../support/itemLifecycle';
import type {ClothingItemEditData} from '../components/item-form/types';

/**
 * Значения формы вещи. Все поля — в «форменном» виде: строки для input/select,
 * массивы id для чипов. Приведение к API-типам — в `buildItemPayload`.
 */
export interface ItemFormValues {
    name: string;
    description: string;
    categoryId: string;
    brandId: string;
    sizeId: string;
    conditionId: string;
    colorIds: string[];
    purchaseDate: string;
    disposalDate: string;
    priceRub: string;
    layerIds: string[];
    warmthLevelId: string;
    thrownAway: boolean;
    waterProof: boolean;
    seasonIds: string[];
    materialIds: string[];
    occasionIds: string[];
    styleIds: string[];
    weatherSuitabilityIds: string[];
    defectIds: string[];
    printTypeId: string;
    fitId: string;
    formalityId: string;
    purpose: ItemPurpose;
}

/** Поля-множества: их значение переключается чипами. */
export type ItemFormMultiField =
    | 'colorIds' | 'layerIds' | 'seasonIds' | 'materialIds'
    | 'occasionIds' | 'styleIds' | 'weatherSuitabilityIds' | 'defectIds';

/** Поля, у которых бывает собственная ошибка валидации. */
export type ItemFormErrorField = 'name' | 'priceRub';

export type ItemFormErrors = Partial<Record<ItemFormErrorField, string>>;

export const EMPTY_ITEM_FORM_VALUES: ItemFormValues = {
    name: '',
    description: '',
    categoryId: '',
    brandId: '',
    sizeId: '',
    conditionId: '',
    colorIds: [],
    purchaseDate: '',
    disposalDate: '',
    priceRub: '',
    layerIds: [],
    warmthLevelId: '',
    thrownAway: false,
    waterProof: false,
    seasonIds: [],
    materialIds: [],
    occasionIds: [],
    styleIds: [],
    weatherSuitabilityIds: [],
    defectIds: [],
    printTypeId: '',
    fitId: '',
    formalityId: '',
    purpose: 'EVERYDAY',
};

/** Значения формы из вещи (редактирование либо проверка результата AI). */
export function valuesFromEditItem(item: ClothingItemEditData): ItemFormValues {
    return {
        ...EMPTY_ITEM_FORM_VALUES,
        name: item.name ?? '',
        description: item.description ?? '',
        categoryId: item.categoryId ?? '',
        brandId: item.brandId ?? '',
        sizeId: item.sizeId ?? '',
        conditionId: item.conditionId ?? '',
        colorIds: item.colorIds ?? [],
        purchaseDate: item.purchaseDate ? item.purchaseDate.slice(0, 10) : '',
        disposalDate: item.disposalDate ? item.disposalDate.slice(0, 10) : '',
        priceRub: item.priceRub != null ? String(item.priceRub) : '',
        layerIds: item.layerIds ?? [],
        warmthLevelId: item.warmthLevelId ?? '',
        thrownAway: item.thrownAway ?? false,
        waterProof: item.waterProof ?? false,
        seasonIds: item.seasonIds ?? [],
        materialIds: item.materialIds ?? [],
        occasionIds: item.occasionIds ?? [],
        styleIds: item.styleIds ?? [],
        weatherSuitabilityIds: item.weatherSuitabilityIds ?? [],
        defectIds: item.defectIds ?? [],
        printTypeId: item.printTypeId ?? '',
        fitId: item.fitId ?? '',
        formalityId: item.formalityId ?? '',
        purpose: item.purpose ?? 'EVERYDAY',
    };
}

/**
 * Значения формы при создании новой вещи. Предзаполнение (CTHS-200) приходит
 * извне — из оценки образа или списка покупок; стили из предпочтений
 * пользователя сюда НЕ подставляются: вещь описывает себя, а не вкус владельца
 * (QA-13, решение владельца от 2026-09-15).
 */
export function valuesFromPrefill(prefill?: Partial<ClothingItemEditData> | null): ItemFormValues {
    if (!prefill) {
        return EMPTY_ITEM_FORM_VALUES;
    }
    return {
        ...EMPTY_ITEM_FORM_VALUES,
        name: prefill.name ?? '',
        description: prefill.description ?? '',
        categoryId: prefill.categoryId ?? '',
        colorIds: prefill.colorIds ?? [],
    };
}

/**
 * Валидация формы. Сообщения — рядом с полем и в summary сверху формы
 * (styleguide §Forms): цвета самого по себе недостаточно.
 */
export function validateItemForm(values: ItemFormValues): ItemFormErrors {
    const errors: ItemFormErrors = {};
    if (!values.name.trim()) {
        errors.name = 'Введите название';
    }
    const price = values.priceRub.trim();
    if (price !== '') {
        const parsed = Number(price);
        if (Number.isNaN(parsed)) {
            errors.priceRub = 'Цена должна быть числом';
        } else if (parsed < 0) {
            errors.priceRub = 'Цена не может быть отрицательной';
        }
    }
    return errors;
}

/** Полезная нагрузка POST/PUT `/clothing/items` и подтверждения распознавания. */
export function buildItemPayload(values: ItemFormValues, photoKeys: string[]) {
    return {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        categoryId: values.categoryId || undefined,
        brandId: values.brandId || undefined,
        sizeId: values.sizeId || undefined,
        conditionId: values.conditionId || undefined,
        colorIds: values.colorIds.length ? values.colorIds : undefined,
        purchaseDate: values.purchaseDate || undefined,
        disposalDate: values.disposalDate || undefined,
        priceRub: values.priceRub ? parseFloat(values.priceRub) : undefined,
        layerIds: values.layerIds.length ? values.layerIds : undefined,
        warmthLevelId: values.warmthLevelId || undefined,
        thrownAway: values.thrownAway,
        waterProof: values.waterProof,
        seasonIds: values.seasonIds.length ? values.seasonIds : undefined,
        materialIds: values.materialIds.length ? values.materialIds : undefined,
        occasionIds: values.occasionIds.length ? values.occasionIds : undefined,
        styleIds: values.styleIds.length ? values.styleIds : undefined,
        weatherSuitabilityIds: values.weatherSuitabilityIds.length ? values.weatherSuitabilityIds : undefined,
        defectIds: values.defectIds.length ? values.defectIds : undefined,
        printTypeId: values.printTypeId || undefined,
        fitId: values.fitId || undefined,
        formalityId: values.formalityId || undefined,
        purpose: values.purpose,
        photoUrls: photoKeys,
    };
}

interface ItemFormState {
    values: ItemFormValues;
    errors: ItemFormErrors;
}

type ItemFormAction =
    | {type: 'setField'; field: keyof ItemFormValues; value: ItemFormValues[keyof ItemFormValues]}
    | {type: 'toggle'; field: ItemFormMultiField; id: string}
    | {type: 'reset'; values: ItemFormValues}
    | {type: 'setErrors'; errors: ItemFormErrors};

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Дата утилизации и флаг «Выброшено» — одно решение пользователя, поэтому их
 * связь живёт в редьюсере, а не в разметке секции.
 */
function coupleDisposal(values: ItemFormValues, field: keyof ItemFormValues): ItemFormValues {
    if (field === 'thrownAway') {
        return {
            ...values,
            disposalDate: values.thrownAway ? (values.disposalDate || todayIso()) : '',
        };
    }
    if (field === 'disposalDate') {
        return {...values, thrownAway: Boolean(values.disposalDate)};
    }
    return values;
}

function reducer(state: ItemFormState, action: ItemFormAction): ItemFormState {
    switch (action.type) {
        case 'setField': {
            const values = coupleDisposal(
                {...state.values, [action.field]: action.value},
                action.field
            );
            // Ошибка поля снимается сразу, как только пользователь его поправил.
            const errors = {...state.errors};
            delete errors[action.field as ItemFormErrorField];
            return {values, errors};
        }
        case 'toggle': {
            const current = state.values[action.field];
            const next = current.includes(action.id)
                ? current.filter(id => id !== action.id)
                : [...current, action.id];
            return {...state, values: {...state.values, [action.field]: next}};
        }
        case 'reset':
            return {values: action.values, errors: {}};
        case 'setErrors':
            return {...state, errors: action.errors};
        default:
            return state;
    }
}

export interface UseItemForm {
    values: ItemFormValues;
    errors: ItemFormErrors;
    setField: <K extends keyof ItemFormValues>(field: K, value: ItemFormValues[K]) => void;
    toggle: (field: ItemFormMultiField, id: string) => void;
    reset: (values: ItemFormValues) => void;
    setErrors: (errors: ItemFormErrors) => void;
    /** Проверяет форму, публикует ошибки и возвращает их вызывающему. */
    validate: () => ItemFormErrors;
}

/**
 * Состояние формы вещи: значения, валидация, предзаполнение. Вынесено из
 * `AddClothingItemModal` (997 строк, 25 `useState`) — контейнер отвечает за
 * режимы и отправку, хук за данные (QA-13).
 */
export function useItemForm(initial: ItemFormValues = EMPTY_ITEM_FORM_VALUES): UseItemForm {
    const [state, dispatch] = useReducer(reducer, {values: initial, errors: {}});

    const setField = useCallback(<K extends keyof ItemFormValues>(field: K, value: ItemFormValues[K]) => {
        dispatch({type: 'setField', field, value});
    }, []);

    const toggle = useCallback((field: ItemFormMultiField, id: string) => {
        dispatch({type: 'toggle', field, id});
    }, []);

    const reset = useCallback((values: ItemFormValues) => {
        dispatch({type: 'reset', values});
    }, []);

    const setErrors = useCallback((errors: ItemFormErrors) => {
        dispatch({type: 'setErrors', errors});
    }, []);

    const {values} = state;
    const validate = useCallback(() => {
        const errors = validateItemForm(values);
        dispatch({type: 'setErrors', errors});
        return errors;
    }, [values]);

    return {values, errors: state.errors, setField, toggle, reset, setErrors, validate};
}

export default useItemForm;
